import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useSkatConnection } from '@features/vatDeclarations/contexts/skatConnectionContext'

import { ReactComponent as MomsLogo } from '../../../assets/images/integrations/moms.svg'
import { useEmberTransitionTo } from '../../../hooks/routing/useEmberTransitionTo'
import { useOrganizationViewUrl } from '../../../hooks/routing/useOrganizationViewUrl'
import { reactRoute } from '../../../utils/routing/reactRoute'
import { IntegrationStatusLabel } from '../elements/IntegrationCard/elements/IntegrationStatusLabel'
import { IntegrationId } from '../enums/integrationId'
import { IntegrationStatus } from '../enums/integrationStatus'
import { IntegrationItem } from '../types/integrationItem'

export const useMomsIntegration = (): IntegrationItem => {
  const { t } = useTranslation()
  const { transitionTo } = useEmberTransitionTo()
  const { isLoading, isSkatConnected } = useSkatConnection()

  const { url: vatDeclarationsUrl } = useOrganizationViewUrl(reactRoute.organization.vatDeclarations.getMainRoute())

  const integrationStatus = useMemo(() => {
    if (isLoading) {
      return null
    }

    return isSkatConnected ? IntegrationStatus.Active : IntegrationStatus.Inactive
  }, [isLoading, isSkatConnected])

  const buttonHandler = useCallback(() => {
    if (
      vatDeclarationsUrl &&
      (integrationStatus === IntegrationStatus.Inactive || integrationStatus === IntegrationStatus.Active)
    ) {
      return transitionTo(vatDeclarationsUrl)
    }
  }, [integrationStatus, transitionTo, vatDeclarationsUrl])

  const buttonText = useMemo(() => {
    if (integrationStatus === IntegrationStatus.Inactive) {
      return t('integrations.card.momsintegration.button.activate')
    } else if (integrationStatus === IntegrationStatus.Active) {
      return t('integrations.card.momsintegration.button.deactivate')
    }
  }, [integrationStatus, t])

  return {
    buttonHandler,
    buttonText,
    description: t('integrations.card.momsintegration.body'),
    id: IntegrationId.Momsintegration,
    imageNode: <MomsLogo />,
    isHidden: false,
    isHiddenLoading: false,
    isStatusLoading: isLoading,
    name: t('integrations.card.momsintegration.title'),
    readMoreLink: 'https://help.shine.co/da/articles/625998-sadan-indberetter-du-moms-til-skat-via-shine-regnskab',
    statusLabelNode: <IntegrationStatusLabel status={integrationStatus} isStatusLoading={isLoading} />,
  }
}
